/**
 * The photometric pipeline: RawFrame -> linear light -> dark/flat correction
 * -> measurement quantity -> photometric calibration.
 *
 * Every number shown or exported by Aperto is produced here; display mapping
 * (window/level, false colour) never feeds back into measurements.
 */
import {
  cctFromXy,
  DEFAULT_TRANSFER_PARAMS,
  hlgSystemGamma,
  lstar,
  makeEotf,
  PRIMARIES,
  rgbToXyzMatrix,
  transferIsAbsolute,
  xyzToUpVp,
  xyzToXy,
  type Chromaticities,
  type Mat3,
  type TransferParams,
} from './color';
import { buildCalibration, DEFAULT_CALIBRATION, type CalibrationFit, type PhotometricCalibration } from './calibration';
import { CLIP_HIGH, CLIP_LOW, type Plane, type PrimariesId, type RawFrame, type TransferId } from './types';

export type QuantityId =
  'Y' | 'X' | 'Z' | 'R' | 'G' | 'B' | 'Lstar' | 'x' | 'y' | 'up' | 'vp' | 'CCT' | 'Duv' | 'luma' | 'codeR' | 'codeG' | 'codeB';

export type QuantityKind = 'radiometric' | 'lightness' | 'chroma' | 'cct' | 'code';

export interface QuantityInfo {
  id: QuantityId;
  label: string;
  short: string;
  kind: QuantityKind;
}

export const QUANTITIES: QuantityInfo[] = [
  { id: 'Y', label: 'Luminance Y (CIE 1931)', short: 'Y', kind: 'radiometric' },
  { id: 'X', label: 'Tristimulus X', short: 'X', kind: 'radiometric' },
  { id: 'Z', label: 'Tristimulus Z', short: 'Z', kind: 'radiometric' },
  { id: 'R', label: 'Red, linear', short: 'R lin', kind: 'radiometric' },
  { id: 'G', label: 'Green, linear', short: 'G lin', kind: 'radiometric' },
  { id: 'B', label: 'Blue, linear', short: 'B lin', kind: 'radiometric' },
  { id: 'Lstar', label: 'Lightness L* (CIE 1976)', short: 'L*', kind: 'lightness' },
  { id: 'x', label: 'Chromaticity x (CIE 1931)', short: 'x', kind: 'chroma' },
  { id: 'y', label: 'Chromaticity y (CIE 1931)', short: 'y', kind: 'chroma' },
  { id: 'up', label: "Chromaticity u' (CIE 1976)", short: "u'", kind: 'chroma' },
  { id: 'vp', label: "Chromaticity v' (CIE 1976)", short: "v'", kind: 'chroma' },
  { id: 'CCT', label: 'Correlated colour temperature', short: 'CCT', kind: 'cct' },
  { id: 'Duv', label: 'Distance from Planckian locus Duv', short: 'Duv', kind: 'cct' },
  { id: 'luma', label: "Luma Y' (encoded, code values)", short: "Y'", kind: 'code' },
  { id: 'codeR', label: 'Red code value (raw)', short: 'R DN', kind: 'code' },
  { id: 'codeG', label: 'Green code value (raw)', short: 'G DN', kind: 'code' },
  { id: 'codeB', label: 'Blue code value (raw)', short: 'B DN', kind: 'code' },
];

export function quantityInfo(id: QuantityId): QuantityInfo {
  return QUANTITIES.find((q) => q.id === id)!;
}

/** Quantities that are ratios of tristimulus values and must be derived from averaged XYZ. */
export function isDerivedChroma(id: QuantityId): boolean {
  const k = quantityInfo(id).kind;
  return k === 'chroma' || k === 'cct';
}

export type ClipDetection = 'auto' | 'on' | 'off';

/** Whether saturation flags apply to this frame under the given setting. */
export function clipDetectionActive(f: RawFrame, mode: ClipDetection): boolean {
  if (mode !== 'auto') return mode === 'on';
  const o = f.meta.origin;
  return !(o === 'capture' || o === 'live' || o === 'paste');
}

export type DarkMode = 'none' | 'constant' | 'frame';

export interface PipelineSettings {
  transferMode: 'auto' | 'manual';
  transfer: TransferId;
  transferParams: TransferParams;
  primariesMode: 'auto' | 'manual';
  primaries: PrimariesId;
  customPrimaries: Chromaticities;
  darkMode: DarkMode;
  /** Constant dark offset in linear relative units, subtracted from every channel. */
  darkConstant: number;
  flatEnabled: boolean;
  /** Reference white luminance Yn for L* (in relative linear units). */
  lstarWhite: number;
  /**
   * Saturation threshold for float data (normalised). Integer data always uses
   * the maximum code value. Infinity disables detection.
   */
  floatClipLevel: number;
  /**
   * Saturation flagging. 'auto' disables it for screen content (captures and
   * pasted screenshots), where the maximum code is ordinary white, not clipping.
   */
  clipDetection: ClipDetection;
  calibration: PhotometricCalibration;
}

export const DEFAULT_PIPELINE: PipelineSettings = {
  transferMode: 'auto',
  transfer: 'srgb',
  transferParams: { ...DEFAULT_TRANSFER_PARAMS },
  primariesMode: 'auto',
  primaries: 'srgb',
  customPrimaries: structuredClone(PRIMARIES.srgb.c),
  darkMode: 'none',
  darkConstant: 0,
  flatEnabled: false,
  lstarWhite: 1,
  floatClipLevel: Infinity,
  clipDetection: 'auto',
  calibration: structuredClone(DEFAULT_CALIBRATION),
};

export interface ResolvedEncoding {
  transfer: TransferId;
  params: TransferParams;
  primaries: PrimariesId;
  chromaticities: Chromaticities;
  rgbToXyz: Mat3;
  absolute: boolean;
  transferFromAuto: boolean;
  primariesFromAuto: boolean;
}

export function resolveEncoding(frame: RawFrame | null, s: PipelineSettings): ResolvedEncoding {
  const hint = frame?.meta.encoding;
  const autoT = s.transferMode === 'auto' && !!hint;
  const autoP = s.primariesMode === 'auto' && !!hint;
  const transfer = autoT ? hint!.transfer : s.transfer;
  const params = { ...s.transferParams };
  if (autoT && hint!.transfer === 'gamma' && hint!.gamma) params.gamma = hint!.gamma;
  if (hint?.curve) params.curve = hint.curve;
  const primaries = autoP ? hint!.primaries : s.primaries;
  const chromaticities =
    primaries !== 'custom' ? PRIMARIES[primaries].c : autoP && hint!.chromaticities ? hint!.chromaticities : s.customPrimaries;
  return {
    transfer,
    params,
    primaries,
    chromaticities,
    rgbToXyz: rgbToXyzMatrix(chromaticities),
    absolute: transferIsAbsolute(transfer, params),
    transferFromAuto: autoT,
    primariesFromAuto: autoP,
  };
}

/** Pre-computed, frame-independent state for fast per-pixel evaluation. */
export interface PipelineContext {
  frame: RawFrame;
  settings: PipelineSettings;
  enc: ResolvedEncoding;
  cal: CalibrationFit;
  calActive: boolean;
  /** Per-code linearisation LUT for integer frames. */
  lut: Float32Array | null;
  eotf: (v: number) => number;
  /** Linear dark frame (interleaved like the source), if dark mode = 'frame'. */
  dark: Float32Array | null;
  /** Normalised linear flat field (mean 1 per channel), if enabled. */
  flat: Float32Array | null;
  hlgGamma: number;
  errors: string[];
}

export interface AuxFrames {
  dark?: RawFrame | null;
  flat?: RawFrame | null;
}

/** Linearise a whole frame into an interleaved Float32Array (used for dark and flat frames). */
export function linearizeFrame(frame: RawFrame, enc: ResolvedEncoding): Float32Array {
  const ctx = makeContext(frame, { ...DEFAULT_PIPELINE, darkMode: 'none', flatEnabled: false }, {}, enc);
  const n = frame.width * frame.height;
  const out = new Float32Array(n * frame.channels);
  const rgb = [0, 0, 0];
  for (let i = 0; i < n; i++) {
    linearAt(ctx, i, rgb);
    if (frame.channels === 1) out[i] = rgb[0];
    else {
      out[i * 3] = rgb[0];
      out[i * 3 + 1] = rgb[1];
      out[i * 3 + 2] = rgb[2];
    }
  }
  return out;
}

export function makeContext(
  frame: RawFrame,
  settings: PipelineSettings,
  aux: AuxFrames = {},
  encOverride?: ResolvedEncoding,
): PipelineContext {
  const enc = encOverride ?? resolveEncoding(frame, settings);
  const eotf = makeEotf(enc.transfer, enc.params);
  let lut: Float32Array | null = null;
  if (!frame.isFloat && frame.maxCode <= 65535) {
    lut = new Float32Array(frame.maxCode + 1);
    for (let c = 0; c <= frame.maxCode; c++) lut[c] = eotf(c * frame.scale);
  }
  const cal = buildCalibration(settings.calibration);
  const errors: string[] = [];
  if (cal.error) errors.push(cal.error);
  const ctx: PipelineContext = {
    frame,
    settings,
    enc,
    cal,
    calActive: settings.calibration.mode !== 'none' && !cal.error,
    lut,
    eotf,
    dark: null,
    flat: null,
    hlgGamma: hlgSystemGamma(enc.params.hlgPeak),
    errors,
  };
  const sameSize = (f: RawFrame) => f.width === frame.width && f.height === frame.height;
  if (settings.darkMode === 'frame') {
    if (!aux.dark) errors.push('Dark-frame subtraction is enabled but no dark frame is loaded');
    else if (!sameSize(aux.dark)) errors.push('Dark frame size does not match the image');
    else
      ctx.dark = cachedAux(aux.dark, enc, frame.channels, 'dark', () =>
        expandChannels(linearizeFrame(aux.dark!, enc), aux.dark!.channels, frame.channels),
      );
  }
  if (settings.flatEnabled) {
    if (!aux.flat) errors.push('Flat-field correction is enabled but no flat frame is loaded');
    else if (!sameSize(aux.flat)) errors.push('Flat frame size does not match the image');
    else {
      const dark = ctx.dark;
      ctx.flat = cachedAux(aux.flat, enc, frame.channels, `flat:${aux.dark && dark ? frameId(aux.dark) : ''}`, () => {
        const flat = expandChannels(linearizeFrame(aux.flat!, enc), aux.flat!.channels, frame.channels);
        if (dark) for (let i = 0; i < flat.length; i++) flat[i] -= dark[i];
        normalizeFlat(flat, frame.channels);
        return flat;
      });
    }
  }
  return ctx;
}

const frameIds = new WeakMap<RawFrame, number>();
let nextFrameId = 1;
function frameId(f: RawFrame): number {
  let id = frameIds.get(f);
  if (!id) frameIds.set(f, (id = nextFrameId++));
  return id;
}

/** Linearised auxiliary frames are cached per frame and encoding so settings changes stay cheap. */
const auxCache = new WeakMap<RawFrame, Map<string, Float32Array>>();

function cachedAux(f: RawFrame, enc: ResolvedEncoding, ch: number, kind: string, make: () => Float32Array): Float32Array {
  const key = `${kind}|${ch}|${enc.transfer}|${JSON.stringify(enc.params)}|${enc.primaries}`;
  let m = auxCache.get(f);
  if (!m) auxCache.set(f, (m = new Map()));
  let v = m.get(key);
  if (!v) {
    v = make();
    m.clear();
    m.set(key, v);
  }
  return v;
}

function expandChannels(src: Float32Array, from: number, to: number): Float32Array {
  if (from === to) return src;
  const n = src.length / from;
  const out = new Float32Array(n * to);
  if (from === 1) for (let i = 0; i < n; i++) out[i * 3] = out[i * 3 + 1] = out[i * 3 + 2] = src[i];
  else for (let i = 0; i < n; i++) out[i] = (src[i * 3] + src[i * 3 + 1] + src[i * 3 + 2]) / 3;
  return out;
}

function normalizeFlat(flat: Float32Array, ch: number) {
  for (let c = 0; c < ch; c++) {
    let s = 0;
    let n = 0;
    for (let i = c; i < flat.length; i += ch) {
      const v = flat[i];
      if (Number.isFinite(v)) {
        s += v;
        n++;
      }
    }
    const mean = n ? s / n : 1;
    for (let i = c; i < flat.length; i += ch) {
      const v = flat[i] / mean;
      flat[i] = v > 1e-6 ? v : NaN;
    }
  }
}

/** Linear, corrected RGB of pixel index i (grey frames replicate the channel). */
export function linearAt(ctx: PipelineContext, i: number, out: number[]): void {
  const f = ctx.frame;
  const d = f.data;
  const lut = ctx.lut;
  let r: number, g: number, b: number;
  if (f.channels === 1) {
    const v = lut ? lut[d[i]] : ctx.eotf(d[i] * f.scale);
    r = g = b = v;
  } else {
    const k = i * 3;
    if (lut) {
      r = lut[d[k]];
      g = lut[d[k + 1]];
      b = lut[d[k + 2]];
    } else {
      r = ctx.eotf(d[k] * f.scale);
      g = ctx.eotf(d[k + 1] * f.scale);
      b = ctx.eotf(d[k + 2] * f.scale);
    }
  }
  if (ctx.enc.transfer === 'hlg' && ctx.enc.params.hlgOotf) {
    // BT.2100 HLG OOTF: Fd = Lw * Ys^(gamma-1) * E, with Ys from the BT.2020 luminance weights.
    const ys = 0.2627 * r + 0.678 * g + 0.0593 * b;
    const k = ctx.enc.params.hlgPeak * Math.pow(Math.max(ys, 0), ctx.hlgGamma - 1);
    r *= k;
    g *= k;
    b *= k;
  }
  const s = ctx.settings;
  if (s.darkMode === 'constant') {
    r -= s.darkConstant;
    g -= s.darkConstant;
    b -= s.darkConstant;
  } else if (ctx.dark) {
    if (f.channels === 1) {
      r -= ctx.dark[i];
      g = b = r;
    } else {
      r -= ctx.dark[i * 3];
      g -= ctx.dark[i * 3 + 1];
      b -= ctx.dark[i * 3 + 2];
    }
  }
  if (ctx.flat) {
    if (f.channels === 1) {
      r /= ctx.flat[i];
      g = b = r;
    } else {
      r /= ctx.flat[i * 3];
      g /= ctx.flat[i * 3 + 1];
      b /= ctx.flat[i * 3 + 2];
    }
  }
  out[0] = r;
  out[1] = g;
  out[2] = b;
}

/** Raw code values of pixel i (grey frames replicate). Float data is reported as-is. */
export function codesAt(f: RawFrame, i: number, out: number[]): void {
  if (f.channels === 1) out[0] = out[1] = out[2] = f.data[i];
  else {
    out[0] = f.data[i * 3];
    out[1] = f.data[i * 3 + 1];
    out[2] = f.data[i * 3 + 2];
  }
}

export type QuantityFn = (ctx: PipelineContext, i: number) => number;

/** Build a per-pixel evaluator for a quantity. */
export function quantityFn(q: QuantityId, ctx: PipelineContext): QuantityFn {
  const m = ctx.enc.rgbToXyz;
  const rgb = [0, 0, 0];
  const f = ctx.cal.f;
  const calActive = ctx.calActive;
  // Non-Y radiometric quantities scale by f(Y)/Y, which preserves chromaticity.
  // Where Y <= 0 (noise after dark subtraction) that ratio is undefined, so use the local slope.
  const slope = calActive ? (f(1e-3) - f(0)) / 1e-3 : 1;
  const ratio = (Y: number) => {
    if (!calActive) return 1;
    return Y > 0 ? f(Y) / Y : slope;
  };
  switch (q) {
    case 'Y':
      return (c, i) => {
        linearAt(c, i, rgb);
        const Y = m[3] * rgb[0] + m[4] * rgb[1] + m[5] * rgb[2];
        return calActive ? f(Y) : Y;
      };
    case 'X':
    case 'Z': {
      const row = q === 'X' ? 0 : 6;
      return (c, i) => {
        linearAt(c, i, rgb);
        const Y = m[3] * rgb[0] + m[4] * rgb[1] + m[5] * rgb[2];
        return (m[row] * rgb[0] + m[row + 1] * rgb[1] + m[row + 2] * rgb[2]) * ratio(Y);
      };
    }
    case 'R':
    case 'G':
    case 'B': {
      const k = q === 'R' ? 0 : q === 'G' ? 1 : 2;
      return (c, i) => {
        linearAt(c, i, rgb);
        const Y = m[3] * rgb[0] + m[4] * rgb[1] + m[5] * rgb[2];
        return rgb[k] * ratio(Y);
      };
    }
    case 'Lstar': {
      const yn = ctx.settings.lstarWhite;
      return (c, i) => {
        linearAt(c, i, rgb);
        return lstar(m[3] * rgb[0] + m[4] * rgb[1] + m[5] * rgb[2], yn);
      };
    }
    case 'x':
    case 'y':
    case 'up':
    case 'vp':
    case 'CCT':
    case 'Duv':
      return (c, i) => {
        linearAt(c, i, rgb);
        const X = m[0] * rgb[0] + m[1] * rgb[1] + m[2] * rgb[2];
        const Y = m[3] * rgb[0] + m[4] * rgb[1] + m[5] * rgb[2];
        const Z = m[6] * rgb[0] + m[7] * rgb[1] + m[8] * rgb[2];
        return chromaFromXyz(q, X, Y, Z);
      };
    case 'luma':
      return (c, i) => {
        codesAt(c.frame, i, rgb);
        return m[3] * rgb[0] + m[4] * rgb[1] + m[5] * rgb[2];
      };
    case 'codeR':
    case 'codeG':
    case 'codeB': {
      const k = q === 'codeR' ? 0 : q === 'codeG' ? 1 : 2;
      return (c, i) => {
        codesAt(c.frame, i, rgb);
        return rgb[k];
      };
    }
  }
}

/** Chromaticity-type quantity from tristimulus values. */
export function chromaFromXyz(q: QuantityId, X: number, Y: number, Z: number): number {
  switch (q) {
    case 'x':
      return xyzToXy(X, Y, Z)[0];
    case 'y':
      return xyzToXy(X, Y, Z)[1];
    case 'up':
      return xyzToUpVp(X, Y, Z)[0];
    case 'vp':
      return xyzToUpVp(X, Y, Z)[1];
    case 'CCT':
    case 'Duv': {
      const [x, y] = xyzToXy(X, Y, Z);
      const r = cctFromXy(x, y);
      if (!r.valid) return NaN;
      return q === 'CCT' ? r.cct : r.duv;
    }
    default:
      return NaN;
  }
}

/** Evaluate a quantity over a rectangular region (clipped to the image) into a Plane. */
export function computePlane(ctx: PipelineContext, q: QuantityId, region?: { x: number; y: number; w: number; h: number }): Plane {
  const f = ctx.frame;
  const x0 = Math.max(0, Math.floor(region?.x ?? 0));
  const y0 = Math.max(0, Math.floor(region?.y ?? 0));
  const x1 = Math.min(f.width, Math.ceil(region ? region.x + region.w : f.width));
  const y1 = Math.min(f.height, Math.ceil(region ? region.y + region.h : f.height));
  const w = Math.max(0, x1 - x0);
  const h = Math.max(0, y1 - y0);
  const data = new Float32Array(w * h);
  const fn = quantityFn(q, ctx);
  for (let y = 0; y < h; y++) {
    const row = (y + y0) * f.width + x0;
    const o = y * w;
    for (let x = 0; x < w; x++) data[o + x] = fn(ctx, row + x);
  }
  return { data, width: w, height: h, ox: x0, oy: y0, fullWidth: f.width, fullHeight: f.height };
}

/** Saturation mask: CLIP_HIGH where any channel reaches the clip level, CLIP_LOW where all channels are 0. */
export function computeClipMask(f: RawFrame, floatClipLevel = Infinity): Uint8Array {
  const n = f.width * f.height;
  const mask = new Uint8Array(n);
  const hi = f.isFloat ? Math.min(f.clipLevel, floatClipLevel) : f.maxCode;
  const d = f.data;
  const ch = f.channels;
  for (let i = 0; i < n; i++) {
    let high = false;
    let low = true;
    for (let c = 0; c < ch; c++) {
      const v = d[i * ch + c];
      if (v >= hi) high = true;
      if (v > 0) low = false;
    }
    mask[i] = (high ? CLIP_HIGH : 0) | (low ? CLIP_LOW : 0);
  }
  return mask;
}

export interface UnitInfo {
  unit: string;
  /** Axis label, e.g. "Luminance Y [cd/m²]". */
  axis: string;
}

export function quantityUnit(q: QuantityId, ctx: { enc: ResolvedEncoding; calActive: boolean; settings: PipelineSettings }): UnitInfo {
  const info = quantityInfo(q);
  let unit = '';
  switch (info.kind) {
    case 'radiometric':
      unit = ctx.calActive ? ctx.settings.calibration.unit : ctx.enc.absolute ? 'cd/m²' : 'rel.';
      break;
    case 'code':
      unit = 'DN';
      break;
    case 'cct':
      unit = q === 'CCT' ? 'K' : '';
      break;
    default:
      unit = '';
  }
  return { unit, axis: unit ? `${info.short} [${unit}]` : info.short };
}

export interface PixelReadout {
  x: number;
  y: number;
  codes: [number, number, number];
  linear: [number, number, number];
  XYZ: [number, number, number];
  chroma: [number, number];
  value: number;
  clip: number;
}

/** Everything known about one pixel, for the probe / inspector. */
export function readPixel(ctx: PipelineContext, q: QuantityId, ix: number, iy: number, clipMask?: Uint8Array | null): PixelReadout | null {
  const f = ctx.frame;
  if (ix < 0 || iy < 0 || ix >= f.width || iy >= f.height) return null;
  const i = iy * f.width + ix;
  const codes = [0, 0, 0];
  const lin = [0, 0, 0];
  codesAt(f, i, codes);
  linearAt(ctx, i, lin);
  const m = ctx.enc.rgbToXyz;
  const XYZ: [number, number, number] = [
    m[0] * lin[0] + m[1] * lin[1] + m[2] * lin[2],
    m[3] * lin[0] + m[4] * lin[1] + m[5] * lin[2],
    m[6] * lin[0] + m[7] * lin[1] + m[8] * lin[2],
  ];
  return {
    x: ix,
    y: iy,
    codes: codes as [number, number, number],
    linear: lin as [number, number, number],
    XYZ,
    chroma: xyzToXy(...XYZ),
    value: quantityFn(q, ctx)(ctx, i),
    clip: clipMask ? clipMask[i] : 0,
  };
}
